'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  IconButton,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FiSearch } from '@react-icons/all-files/fi/FiSearch';
import { FiX } from '@react-icons/all-files/fi/FiX';
import { GeneralSearchInput } from './styles';
import { ResultsByCategory } from './components/results-by-catgory';
import debounce from 'lodash.debounce';
import { GeneralSearchProvider } from './context';
import { DialogTransition } from '../dialog-transition';
import { ICategory, IGeneralSearchProps } from './interface';
import { IAssetsByCategory } from '@mais-retorno-front/core-business/useCases/search-asset/interface';
import { GeneralSearchSheetsUseCase } from '@core/design-system/search';

export const GeneralSearch = ({
  values = [],
  comparisonMode = false,
  incomeFixed = false,
  forwardRef,
  useCase,
  focused = false,
  dialogMode = false,
  isHome = false,
  isOpen,
  onClose = () => {},
  onAdd = ([], {}) => {},
  onRemove = ([], {}) => {},
  placeholder = 'Pesquisar ações, fundos e outros',
}: IGeneralSearchProps) => {
  const [data, setData] = useState<IAssetsByCategory>({} as IAssetsByCategory);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [categories, setCategories] = useState<ICategory[]>([
    { label: 'Tudo', disabled: false, slug: 'all' },
  ]);

  const dialogControl = dialogMode ? isOpen : openDialog;
  const refControl = forwardRef ?? useRef(null);

  const theme = useTheme();

  const isSmDown = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );

  const fetchData = async (term: string) => {
    if (useCase) {
      const { categories: generalCategories, groupedAssets } =
        await useCase.execute(term);

      setCategories(generalCategories);
      setData(groupedAssets);
    } else {
      const { categories, groupedAssets } =
        await new GeneralSearchSheetsUseCase(comparisonMode, true).execute(
          term
        );

      const formattedAssetsList = Object.entries(groupedAssets).reduce(
        (acc, [category, list]) => {
          return {
            ...acc,
            [category]: list.map(
              (asset) =>
                ({
                  ...asset.toJson(),
                  url: asset.canonical_url,
                  enable: !asset.isDisabled(comparisonMode),
                } as any)
            ),
          };
        },
        {}
      );

      setData(formattedAssetsList);
      setCategories(categories);
    }

    setIsLoading(false);
  };

  const delayedFetchData = useCallback(debounce(fetchData, 1500), []);

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;

    if (value.length < 2) return setOpen(false);

    setOpen(true);
    setIsLoading(true);
    delayedFetchData(value);
  };

  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (isSmDown) {
      handleOpenDialog();
      refControl.current?.blur();
    }

    const { value } = e.target;

    if (value.length >= 2) return setOpen(true);
  };

  const handleCloseDialog = (e) => {
    if (dialogMode) {
      return onClose();
    }
    return setOpenDialog(false);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleOpen = (status: boolean) => {
    setOpen(status);
  };

  const closeAll = useCallback(() => {
    setOpenDialog(false);
    setOpen(false);
  }, []);

  const clearInput = () => {
    refControl.current.value = '';
  };

  useEffect(() => {
    if (focused) {
      refControl.current.focus();
    }
  }, [focused]);

  return (
    <>
      <GeneralSearchProvider onAdd={onAdd} onRemove={onRemove} values={values}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box displayPrint="none" position="relative" hidden={dialogMode}>
            <GeneralSearchInput
              inputRef={refControl}
              amendmode={open.toString()}
              onChange={(e) => handleChange(e)}
              onFocus={(e) => handleFocus(e)}
              placeholder={placeholder}
              startAdornment={<FiSearch size={20} />}
              endAdornment={isLoading && <CircularProgress size={20} />}
              sx={{
                backgroundColor: isHome && '#F6F6F61A',
                boxShadow: isHome
                  ? null
                  : '0 1px 3px rgb(60 63 66 / 32%), 0 4px 12px rgb(60 63 66 / 15%);',
                color: isHome && '#fff',
              }}
            />
            <Box
              position="absolute"
              width="100%"
              zIndex={theme.zIndex.mobileStepper}
            >
              <ResultsByCategory
                results={data}
                categories={categories}
                loading={isLoading}
                open={open}
                onOpen={handleOpen}
                floatingMode
                comparisonMode={comparisonMode}
                incomeFixed={incomeFixed}
                handleCloseDialog={closeAll}
                clearInput={clearInput}
              />
            </Box>
          </Box>
        </ClickAwayListener>

        <Dialog
          fullScreen={isSmDown}
          fullWidth={dialogMode}
          {...(dialogMode && { maxWidth: 'md' })}
          open={dialogControl}
          onClose={handleCloseDialog}
          TransitionComponent={DialogTransition}
          sx={(theme) => ({
            [theme.breakpoints.up('sm')]: {
              '& .MuiDialog-paper ': {
                position: 'absolute',
                top: '20%',
              },
            },
          })}
        >
          <Box display="flex" justifyContent="flex-end">
            <GeneralSearchInput
              autoFocus
              placeholder={placeholder}
              onChange={(e) => handleChange(e)}
              startAdornment={<FiSearch size={20} />}
              endAdornment={
                <IconButton onClick={handleCloseDialog} size="large">
                  {isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <FiX size={22} />
                  )}
                </IconButton>
              }
            />
          </Box>
          <ResultsByCategory
            results={data}
            dialogMode={dialogMode}
            categories={categories}
            loading={isLoading}
            open={true}
            floatingMode={!isSmDown}
            comparisonMode={comparisonMode}
            incomeFixed={incomeFixed}
            handleCloseDialog={closeAll}
            clearInput={clearInput}
          />
        </Dialog>
      </GeneralSearchProvider>
    </>
  );
};
